/**
 * Русские названия для перечислений контракта — теперь через каталог строк.
 *
 * Одно понятие — одна строка (voice.md): термин, набранный на двух экранах
 * по-разному, — это тот же класс ошибки, что два разных балла. Поэтому все
 * подписи к enum-ам живут здесь, а экраны их только берут.
 *
 * **Каждая подпись — функция, а не таблица** (B5, образец — `funnelStage`).
 * Таблица `Record<…, string>` считается один раз, при вычислении модуля, то
 * есть до того, как известна локаль вошедшего: переключение языка её не
 * трогает. Функция спрашивает каталог в момент отрисовки. Ключ каталога несёт
 * значение перечисления как есть (`label.run-kind.weekly`) — по тому же
 * правилу, что `error.code.<код>`: часть после последней точки не наша, она
 * пишется ровно так, как приходит с сервера.
 *
 * Порядки (`FUNNEL_ORDER`, `MEMORY_VERDICT_ORDER`) остаются таблицами: порядок
 * не строка и от языка не зависит.
 *
 * Чего здесь нет и не будет: `EngineCode`. Движок — строка справочника, а не
 * перечисление (новый подключается за день без релиза фронта), и его название
 * приходит из `GET /engines` полем `title`. Зашитая таблица движков разъехалась
 * бы с каталогом молча.
 */

import { t } from '../i18n';

import type { Guarded, SectionGroup } from '../sections';
import type { components } from '../api/schema';

type FunnelStage = components['schemas']['FunnelStage'];
type AliasKind = components['schemas']['AliasKind'];
type NameSource = components['schemas']['NameSource'];
type QuestionSource = components['schemas']['QuestionSource'];
type CompetitorSource = components['schemas']['CompetitorSource'];
type RunKind = components['schemas']['RunKind'];
type MemoryVerdict = components['schemas']['MemoryVerdict'];
type ReportKind = components['schemas']['ReportKind'];
type RunState = components['schemas']['RunState'];
type BrandStatus = components['schemas']['BrandStatus'];
type SeriesBasis = components['schemas']['SeriesBasis'];
type TimelineEventKind = components['schemas']['TimelineEventKind'];
type Sentiment = components['schemas']['Sentiment'];
type ParserKind = components['schemas']['ParserKind'];

/**
 * Стадии воронки — в порядке воронки, а не по алфавиту. Порядок несёт смысл:
 * вопрос стадии «решение» стоит дороже вопроса стадии «узнавание», и клиент
 * должен видеть, чем набит его тариф.
 */
export const FUNNEL_ORDER: readonly FunnelStage[] = [
  'awareness',
  'consideration',
  'comparison',
  'decision',
  'reputation',
];

/** Название стадии. */
export function funnelStage(stage: FunnelStage): string {
  return t(`funnel.${stage}`);
}

/** Что покупатель делает на этой стадии. Подсказка под выбором, не название. */
export function funnelStageNote(stage: FunnelStage): string {
  return t(`funnel.${stage}.note`);
}

/**
 * Откуда у бренда его название — одной строкой в карточке.
 *
 * Разница не косметическая: имя `manual` разбор сайта не тронет никогда, имя
 * `domain` он обязан заменить, имя `site` вправе уточнить. Человек, который
 * этого не видит, не понимает, почему одно название сменилось само, а другое
 * так и осталось меткой адреса.
 */
export function nameSource(source: NameSource): string {
  return t(`label.name-source.${source}`);
}

export function aliasKind(kind: AliasKind): string {
  return t(`label.alias-kind.${kind}`);
}

/**
 * Чем «Головная сеть» отличается от сокращения — одной строкой для человека.
 *
 * Сокращение принадлежит одной компании, имя сети — всем её точкам сразу:
 * «Автодом» стоит в ответе про «Автодом Таганка», и без этого рода такое
 * упоминание бренду не засчитывалось. Заводит его разбор сайта и только в тех
 * отраслях, где зачёт верен по смыслу, — поэтому строка показывается рядом с
 * самим синонимом, а не как общее обещание продукта.
 */
export function parentAliasNote(): string {
  return t('label.parent-alias-note');
}

/**
 * Тональность упоминания. Прилагательное, а не существительное: строка
 * дописывается к «Назван, место 3» и читается как продолжение фразы, а не как
 * заголовок графы.
 */
export function sentimentWord(value: Sentiment): string {
  return t(`label.sentiment.${value}`);
}

/**
 * Чем прочитан ответ движка. Разница не техническая: запасной разбор находит
 * вхождение имени и МЕСТА В СПИСКЕ НЕ СТАВИТ, поэтому у такого упоминания место
 * пустое всегда — и без этой строки пустое место читается как «назвали
 * последними».
 */
export function parserKind(kind: ParserKind): string {
  return t(`label.parser.${kind}`);
}

export function questionSource(source: QuestionSource): string {
  return t(`label.question-source.${source}`);
}

export function competitorSource(source: CompetitorSource): string {
  return t(`label.competitor-source.${source}`);
}

/**
 * Вид прогона. `pulse` оставлен по той же причине, что и в `seriesBasis` ниже:
 * пульса в продукте больше нет, но прогоны, отработавшие по нему раньше, лежат
 * в истории, и подписать их «недельной переписью» значило бы соврать о том, как
 * снято число. Продать или включить пульс в кабинете нельзя нигде.
 */
export function runKind(kind: RunKind): string {
  return t(`label.run-kind.${kind}`);
}

/**
 * Четыре вердикта диагноза «знают или находят».
 *
 * Один и тот же вопрос задан движку дважды — с веб-поиском и без него, — и из
 * пары ответов «нас не называют» распадается на четыре разных случая. Разница
 * между вторым и третьим и есть смысл всей затеи: это работа на месяцы вперёд, и
 * работа разная.
 *
 * Слова «модель» здесь нет намеренно — в интерфейсе его нет вовсе (voice.md).
 * Движок «знает» бренд или «не знает»: это то же утверждение человеческим
 * языком.
 */
export function memoryVerdict(verdict: MemoryVerdict): string {
  return t(`label.memory-verdict.${verdict}`);
}

/**
 * Что именно случилось в паре ответов — и только потом, у двух средних случаев,
 * куда это ведёт работу. Сверх наблюдения здесь сказано только направление
 * работы, и только там, где оно следует из самой пары.
 */
export function memoryVerdictNote(verdict: MemoryVerdict): string {
  return t(`label.memory-verdict-note.${verdict}`);
}

/** Порядок вердиктов: от лучшего к худшему, а не по алфавиту. */
export const MEMORY_VERDICT_ORDER: readonly MemoryVerdict[] = [
  'known_and_found',
  'known_not_found',
  'found_via_search',
  'absent',
];

/**
 * Вид отчёта. Форм две, и обе живут здесь, потому что порознь они разошлись:
 * в кабинете было «Для клиента агентства», а на публичной ссылке того же
 * отчёта — «Отчёт агентства». Клиент присылает ссылку заказчику и читает два
 * разных названия одного документа.
 *
 * `reportKind` — короткая форма для колонки «Вид», где слово «отчёт» уже стоит
 * в заголовке таблицы. `reportKindTitle` — полная, для заголовка страницы.
 */
export function reportKind(kind: ReportKind): string {
  return t(`label.report-kind.${kind}`);
}

export function reportKindTitle(kind: ReportKind): string {
  return t(`label.report-kind-title.${kind}`);
}

/**
 * Подпись ряда под числом.
 *
 * Ежедневный пульс из продукта убран: замеряем еженедельно, и ни продать, ни
 * включить пульс в кабинете больше нельзя. Но перечисление контракта знает оба
 * значения, и строки, посчитанные по пульсу раньше, никуда не делись — подписать
 * их «недельной переписью» значило бы соврать про знаменатель. Поэтому подпись
 * остаётся, а обещания её больше нигде нет. Снимать значение — вместе с
 * контрактом, отдельным PR в seeno-ru-api.
 */
export function seriesBasis(basis: SeriesBasis): string {
  return t(`label.series-basis.${basis}`);
}

/**
 * Состояние прогона словами. `partial` — не «ошибка», а неполная сетка: числа
 * по ней верны, просто охватывают не всё, и называть это падением нельзя.
 */
export function runState(state: RunState): string {
  return t(`label.run-state.${state}`);
}

export function brandStatus(status: BrandStatus): string {
  return t(`label.brand-status.${status}`);
}

/**
 * Отметки событий на графике динамики: ЧТО за граница, а не что именно
 * случилось. Подпись стоит у волосяной вертикали моношрифтом, и целиком
 * заголовок события в неё не влезает — заголовок приходит из данных и виден
 * подсказкой на самой отметке.
 *
 * `engine_search_change` — сознательно НЕ «нейросеть движка»: модель при этом не
 * менялась, изменились условия вопроса. За такой отметкой ноль упоминаний по
 * движку означает уже другое — «нет ни в знаниях модели, ни в найденных
 * страницах», — и специалист, отправленный искать смену модели, потерял бы день.
 */
export function timelineEventKind(kind: TimelineEventKind): string {
  return t(`label.timeline.${kind}`);
}

/** Идёт ли прогон прямо сейчас. Опрашивать имеет смысл только эти два. */
export function isRunning(state: RunState): boolean {
  return state === 'queued' || state === 'running';
}

/**
 * Название раздела кабинета на языке кабинета.
 *
 * Живёт здесь, а не в `sections.ts`, намеренно: тот файл обязан оставаться без
 * единого импорта времени выполнения — его загружает голым Node приёмка
 * `check-access.mjs`, а `t()` тянет за собой каталоги и ICU.
 */
export function sectionTitle(guarded: Guarded): string {
  return t(guarded.titleKey);
}

/** Название группы разделов на языке кабинета. */
export function groupTitle(group: SectionGroup): string {
  return t(group.titleKey);
}
